export type CompanyMagicEntryDocumentType =
  | 'registration'
  | 'cor'
  | 'mayors_permit'
  | 'barangay_business_permit'
  | 'store_front'
  | 'store_left'
  | 'store_right'
  | 'store_inside'
  | 'book_crj'
  | 'book_cdj'
  | 'book_gj'
  | 'book_gl'
  | 'book_sj'
  | 'book_pj'
  | 'atp';

// API Routes
export const API_ROUTES = {
  AUTH: {
    LOGIN: '/auth/login',
    AUTHORIZE: '/auth/authorize',
    TOKEN: '/auth/token',
    REVOKE: '/auth/revoke',
    /** Credential update; Bearer-protected (same path when auth is a separate service). */
    PASSWORD: '/auth/password',
    /** Short-lived JWT for EventSource company event streams. */
    SSE_TOKEN: '/auth/sse-token',
  },
  AUTH_SESSION: {
    EXCHANGE: '/auth/exchange',
    REFRESH: '/auth/refresh-session',
    LOGOUT: '/auth/logout-session',
  },
  CATEGORY: '/category',
  CATEGORY_VOIDED: '/category/voided',
  CATEGORY_RESTORE: (id: string) => `/category/${id}/restore`,
  CATEGORY_DEFAULT_CATEGORIES: '/category/default-categories',
  CATEGORY_ACCOUNT_NUMBERS_BULK_UPDATE: '/category/account-numbers/bulk-update',
  CATEGORY_COUNTS_AS_CASH_BULK_UPDATE: '/category/counts-as-cash/bulk-update',
  /** POST body: `{ targetCategoryId: string }` */
  CATEGORY_MOVE_TRANSACTIONS: (categoryId: string) =>
    `/category/${categoryId}/move-transactions`,
  CATEGORY_BATCH: '/category/batch',
  CATEGORY_BATCH_WITH_DEPENDENCIES: '/category/batch-with-dependencies',
  TRANSACTION: '/transactions',
  TRANSACTION_DISPLAY_ORDER: '/transactions/display-order',
  TRANSACTION_RESEQUENCE_JV_REFERENCES:
    '/transactions/resequence-jv-references',
  TRANSACTION_BATCH: '/transactions/batch',
  TRANSACTION_IMPORT_CSV: '/transactions/import/csv',
  TRANSACTION_EXPORT_CSV: '/transactions/export/csv',
  TRANSACTION_FROM_RECEIPT: '/transactions/from-receipt',
  FX_RATE: '/fx/rate',
  FX_CURRENCIES: '/fx/currencies',
  TRANSACTION_RECEIPT_UPLOAD_USAGE: '/transactions/receipt-upload-usage',
  TRANSACTION_BOOKS_START: '/transactions/books-start',
  TRANSACTION_PERIOD_LOCKS_EFFECTIVE: '/transaction-period-locks/effective',
  TRANSACTION_PERIOD_LOCKS: '/transaction-period-locks',
  TRANSACTION_PERIOD_LOCKS_AUDIT: '/transaction-period-locks/audit',
  TRANSACTION_PERIOD_LOCKS_AUDIT_EXPORT:
    '/transaction-period-locks/audit/export',
  TRANSACTION_PERIOD_LOCK_BY_ID: (id: string) =>
    `/transaction-period-locks/${id}`,
  EXPENSES: '/expenses',
  USER: {
    PROFILE: '/user/profile',
    PREFERENCES: '/user/preferences',
    PROFILE_PICTURE_UPLOAD_URL: '/user/profile-picture/upload-url',
    PROFILE_PICTURE: '/user/profile-picture',
    PROFILE_PICTURE_DOWNLOAD_URL: '/user/profile-picture/download-url',
  },
  INVENTORY_APK: {
    ROOT: '/inventory-apk',
    UPLOAD_URL: '/inventory-apk/upload-url',
    DOWNLOAD_URL: '/inventory-apk/download-url',
    FILE: '/inventory-apk/file',
  },
  COMPANY: '/company',
  COMPANY_SIDEBAR_NOTE: '/company-sidebar-note',
  COMPANY_REQUESTS: '/company-requests',
  companyRequestUpdates: (requestId: string) =>
    `/company-requests/${requestId}/updates`,
  companyRequestUpdate: (requestId: string, updateId: string) =>
    `/company-requests/${requestId}/updates/${updateId}`,
  COMPANY_TEMPORARY_UPLOADS: '/company-temporary-uploads',
  companyTemporaryUpload: (uploadId: string) =>
    `/company-temporary-uploads/${uploadId}`,
  companyTemporaryUploadDownloadUrl: (uploadId: string) =>
    `/company-temporary-uploads/${uploadId}/download-url`,
  COMPANY_MY_COMPANIES: '/company/my-companies',
  COMPANY_MY_COMPANIES_LIST: '/company/my-companies/list',
  companyMyCompany: (companyId: string) => `/company/my-companies/${companyId}`,
  COMPANY_API_KEYS: '/company/api-keys',
  COMPANY_APPS: '/company-apps',
  COMPANY_APP_INSTALL: (appId: string) =>
    `/company-apps/${encodeURIComponent(appId)}/install`,
  COMPANY_APP_UNINSTALL: (appId: string) =>
    `/company-apps/${encodeURIComponent(appId)}/uninstall`,

  COMPANY_LOGO_UPLOAD_URL: '/company/:id/logo/upload-url',
  COMPANY_LOGO: '/company/:id/logo',
  COMPANY_LOGO_DOWNLOAD_URL: '/company/:id/logo/download-url',
  SETTINGS_BY_KEY: (key: string) => `/settings/key/${encodeURIComponent(key)}`,
  SETTINGS_COMPANY_PREFERENCES: '/settings/company-preferences',
  COMPANY_MAGIC_ENTRY_DETAILS: '/company-magic-entry-details',
  COMPANY_MAGIC_ENTRY_DETAILS_DRAFT: '/company-magic-entry-details/draft',
  COMPANY_MAGIC_ENTRY_DETAILS_DOCUMENT_UPLOAD_URL: (
    documentType: CompanyMagicEntryDocumentType,
  ) => `/company-magic-entry-details/documents/${documentType}/upload-url`,
  COMPANY_MAGIC_ENTRY_DETAILS_DOCUMENT: (
    documentType: CompanyMagicEntryDocumentType,
  ) => `/company-magic-entry-details/documents/${documentType}`,
  COMPANY_MAGIC_ENTRY_DETAILS_DOCUMENT_DOWNLOAD_URL: (
    documentType: CompanyMagicEntryDocumentType,
  ) => `/company-magic-entry-details/documents/${documentType}/download-url`,
  COMPANY_DOCUMENTS: '/company-documents',
  COMPANY_DOCUMENT: (documentType: string) =>
    `/company-documents/${encodeURIComponent(documentType)}`,
  COMPANY_DOCUMENT_UPLOAD_URL: (documentType: string) =>
    `/company-documents/${encodeURIComponent(documentType)}/upload-url`,
  COMPANY_DOCUMENT_DOWNLOAD_URL: (documentType: string) =>
    `/company-documents/${encodeURIComponent(documentType)}/download-url`,
  BIR_TAXPAYER_PROFILE: '/bir/taxpayer-profile',
  UPLOAD_ENTRIES: '/upload-entries',
  UPLOAD_ENTRIES_ALL_COMPANIES:
    '/upload-entries/dashboard/all-companies-upload-entries',
  SUPER_DASHBOARD_OVERVIEW: '/super-dashboard/overview',
  SUPER_DASHBOARD_ELIGIBILITY: '/super-dashboard/eligibility',
  SUPER_DASHBOARD_HEALTH_CHECKS: '/super-dashboard/health-checks',
  HEALTH_CHECK: '/company-health-check',
  HEALTH_CHECK_DOCUMENT_OVERRIDE: (documentId: string) =>
    `/company-health-check/documents/${encodeURIComponent(documentId)}/override`,
  SUPER_DASHBOARD_ACTIVITY_LOGS: '/super-dashboard/activity-logs',
  SUPER_DASHBOARD_ACTIVITY_LOGS_ACTION_CATEGORIES:
    '/super-dashboard/activity-logs/action-categories',
  SUPER_DASHBOARD_UPLOADED_ENTRIES_EMAIL_CONFIG:
    '/super-dashboard/uploaded-entries-email-config',
  DASHBOARD_BOOKS_FRESHNESS_FOR_MY_COMPANIES:
    '/dashboard/books-freshness-for-my-companies',
  UPLOAD_ENTRY_UPLOAD_URL: '/upload-entries/upload-url',
  UPLOAD_ENTRY: (entryId: string) => `/upload-entries/${entryId}`,
  UPLOAD_ENTRY_MARK_FAILED: (entryId: string) =>
    `/upload-entries/${entryId}/mark-failed`,
  UPLOAD_ENTRY_ARCHIVE: (entryId: string) =>
    `/upload-entries/${entryId}/archive`,
  UPLOAD_ENTRY_REJECT: (entryId: string) => `/upload-entries/${entryId}/reject`,
  UPLOAD_ENTRY_NOTES: (entryId: string) => `/upload-entries/${entryId}/notes`,
  UPLOAD_ENTRY_RECEIPT_ENCODING: (entryId: string) =>
    `/upload-entries/${entryId}/receipt-encoding`,
  UPLOAD_ENTRY_REPLACE_UPLOAD_URL: (entryId: string) =>
    `/upload-entries/${entryId}/replace-upload-url`,
  UPLOAD_ENTRY_REPLACE_FILE: (entryId: string) =>
    `/upload-entries/${entryId}/replace-file`,
  UPLOAD_ENTRY_DOWNLOAD_URL: (entryId: string) =>
    `/upload-entries/${entryId}/download-url`,
  UPLOAD_ENTRIES_BATCH_COMPLETE: '/upload-entries/batch/complete',
  UPLOAD_ENTRIES_ATTACH_TO_EXISTING: '/upload-entries/attach-to-existing',
  UPLOAD_ENTRIES_EXTRACT_SUGGESTIONS: '/upload-entries/extract-suggestions',
  UPLOAD_ENTRIES_REGISTER_FAILED: '/upload-entries/register-failed',
  CASH_RECEIPTS_JOURNAL: '/cash-receipts-journal',
  CRJ_LEDGER: '/crj-ledger',
  CRJ_LEDGER_INSIGHTS: '/crj-ledger/insights',
  CRJ_LEDGER_INSIGHTS_DISMISS: '/crj-ledger/insights/dismiss',
  CRJ_LEDGER_RESEQUENCE_BOOK_REFERENCES:
    '/crj-ledger/resequence-book-references',
  CDJ_LEDGER: '/cdj-ledger',
  CDJ_LEDGER_RESEQUENCE_BOOK_REFERENCES:
    '/cdj-ledger/resequence-book-references',
  CUSTOMERS: '/customers',
  CUSTOMER_GROUPS: '/customers/groups',
  CUSTOMERS_SEND_EMAILS: '/customers/send-emails',
  CUSTOMERS_EMAIL_TEMPLATES: '/customers/email-templates',
  CUSTOMERS_EMAIL_SEND_JOBS_PENDING: '/customers/email-send-jobs/pending',
  CUSTOMERS_EMAIL_SEND_JOBS_RECENT: '/customers/email-send-jobs/recent',
  CUSTOMERS_EMAIL_SEND_JOB: (jobId: string) =>
    `/customers/email-send-jobs/${jobId}`,
  CUSTOMERS_EMAIL_SEND_JOB_DETAILS: (jobId: string) =>
    `/customers/email-send-jobs/${jobId}/details`,
  CUSTOMERS_EMAIL_SEND_JOB_CANCEL: (jobId: string) =>
    `/customers/email-send-jobs/${jobId}/cancel`,
  CUSTOMERS_EMAIL_TEMPLATE_MARK_USED: (templateId: string) =>
    `/customers/email-templates/${templateId}/mark-used`,
  CUSTOMERS_EMAIL_DRAFT: '/customers/email-draft',
  CUSTOMERS_BULK_OPT_OUT_EMAIL: '/customers/bulk-opt-out-email',
  CUSTOMERS_EMAIL_IMAGE_UPLOAD_URL: '/customers/email-image-upload-url',
  BILL_INVOICE_SETTINGS: '/bill-invoice-settings',
  BILL_INVOICES: '/bill-invoices',
  BILL_INVOICES_PREVIEW: '/bill-invoices/preview',
  BILL_INVOICES_ISSUE: '/bill-invoices/issue',
  BILL_INVOICE_CUSTOMER_OVERRIDE: (customerId: string) =>
    `/bill-invoices/customer-overrides/${customerId}`,
  CUSTOMERS_EMAIL_SEND_JOB_DUPLICATE: (jobId: string) =>
    `/customers/email-send-jobs/${jobId}/duplicate`,
  CUSTOMER_EMAIL_SEND_HISTORY: (customerId: string) =>
    `/customers/${customerId}/email-send-history`,
  USER_GMAIL: {
    CONNECTION: '/user-gmail/connection',
    DEV_CONNECT: '/user-gmail/dev/connect',
    OAUTH_START: '/user-gmail/oauth/start',
    BULK_EMAIL_SIGNATURE: '/user-gmail/bulk-email-signature',
  },
  SUPPLIERS: '/suppliers',
  SUPPLIER_GROUPS: '/suppliers/groups',
  AP_BILLS: '/ap-bills',
  AP_BILLS_TAGS: '/ap-bills/tags',
  AP_BILLS_ALL_PAYMENTS: '/ap-bills/payments',
  AP_BILL_PAYMENT_BY_ID: (paymentId: string) =>
    `/ap-bills/payments/${paymentId}`,
  AP_BILLS_PAYMENT_RUN: '/ap-bills/payment-runs',
  AP_BILLS_FX_REVALUATION_RUN: '/ap-bills/fx-revaluation-runs',
  AP_BILLS_ALL_FX_REVALUATIONS: '/ap-bills/fx-revaluations',
  AP_BILL_FX_REVALUATION_BY_ID: (entryId: string) =>
    `/ap-bills/fx-revaluations/${entryId}`,
  AP_BILL_FX_REVALUATIONS: (billId: string) =>
    `/ap-bills/${billId}/fx-revaluations`,
  AP_BILL_PAYMENTS: (billId: string) => `/ap-bills/${billId}/payments`,
  AP_BILL_PAYMENT: (billId: string, paymentId: string) =>
    `/ap-bills/${billId}/payments/${paymentId}`,
  AP_BILL_PAYMENT_CANCEL: (billId: string, paymentId: string) =>
    `/ap-bills/${billId}/payments/${paymentId}/cancel`,
  AP_BILL_CANCEL: (billId: string) => `/ap-bills/${billId}/cancel`,
  AR_INVOICES: '/ar-invoices',
  AR_INVOICES_SERIES: '/ar-invoices/series',
  AR_INVOICES_PAYMENT_RUN: '/ar-invoices/payment-runs',
  AR_INVOICES_PAYMENTS: '/ar-invoices/payments',
  AR_INVOICE_PAYMENT: (paymentId: string) =>
    `/ar-invoices/payments/${paymentId}`,
  AR_INVOICE_PAYMENT_CANCEL: (paymentId: string) =>
    `/ar-invoices/payments/${paymentId}/cancel`,
  AR_INVOICE_PAYMENT_PRINT: (paymentId: string) =>
    `/ar-invoices/payments/${paymentId}/print`,
  AR_INVOICE: (invoiceId: string) => `/ar-invoices/${invoiceId}`,
  AR_INVOICE_CANCEL: (invoiceId: string) => `/ar-invoices/${invoiceId}/cancel`,
  AR_INVOICE_PRINT: (invoiceId: string) => `/ar-invoices/${invoiceId}/print`,
  /** Public (no auth): AR invoice authenticity by company + verification seal. */
  PUBLIC_AR_INVOICE_VERIFY: (companyId: string, seal: string) =>
    `/public/ar-invoices/${encodeURIComponent(companyId.trim())}/verify/${encodeURIComponent(seal.trim())}`,
  EQUIPMENT: '/equipment',
  EQUIPMENT_TAGS: '/equipment/tags',
  EQUIPMENT_TAG_BY_ID: (tagId: string) => `/equipment/tags/${tagId}`,
  EQUIPMENT_BY_ID: (id: string) => `/equipment/${id}`,
  EQUIPMENT_CANCEL: (id: string) => `/equipment/${id}/cancel`,
  EQUIPMENT_RECORD_DEPRECIATION: (id: string) =>
    `/equipment/${id}/record-depreciation`,
  EQUIPMENT_CANCEL_DEPRECIATION: (id: string, entryId: string) =>
    `/equipment/${id}/depreciation/${entryId}/cancel`,
  EQUIPMENT_DEPRECIATION_REPORT: '/equipment/depreciation-report',
  EQUIPMENT_DEPRECIATION_BY_ID: (entryId: string) =>
    `/equipment/depreciation/${entryId}`,
  EQUIPMENT_FROM_TRANSACTION: (transactionId: string) =>
    `/equipment/from-transaction/${transactionId}`,
  HRIS_EMPLOYEES: '/hris/employees',
  HRIS_EMPLOYEE: (id: string) => `/hris/employees/${id}`,
  HRIS_EMPLOYEE_DOCUMENTS: (id: string) => `/hris/employees/${id}/documents`,
  HRIS_EMPLOYEE_DOCUMENT_UPLOAD_URL: (id: string) =>
    `/hris/employees/${id}/documents/upload-url`,
  HRIS_EMPLOYEE_PENDING_DOCUMENT_UPLOAD_URL:
    '/hris/employees/document-upload-url',
  HRIS_EMPLOYEE_DOCUMENT_DOWNLOAD_URL: (
    employeeId: string,
    documentId: string,
  ) => `/hris/employees/${employeeId}/documents/${documentId}/download-url`,
  HRIS_EMPLOYEE_DOCUMENT: (employeeId: string, documentId: string) =>
    `/hris/employees/${employeeId}/documents/${documentId}`,
  HRIS_EMPLOYEE_EMPLOYMENT_EVENTS: (id: string) =>
    `/hris/employees/${id}/employment-events`,
  HRIS_DEPARTMENTS: '/hris/departments',
  HRIS_DEPARTMENT: (id: string) => `/hris/departments/${id}`,
  HRIS_JOB_TITLES: '/hris/job-titles',
  HRIS_JOB_TITLE: (id: string) => `/hris/job-titles/${id}`,
  HRIS_ATTENDANCE_HOLIDAYS: '/hris/attendance/holidays',
  HRIS_ATTENDANCE_SCHEDULES: '/hris/attendance/schedules',
  HRIS_ATTENDANCE_CLOCK_EVENTS: '/hris/attendance/clock-events',
  HRIS_ATTENDANCE_CLOCK_IN: '/hris/attendance/clock-in',
  HRIS_ATTENDANCE_CLOCK_OUT: '/hris/attendance/clock-out',
  HRIS_ATTENDANCE_TIMESHEETS: '/hris/attendance/timesheets',
  HRIS_ATTENDANCE_TIMESHEET_REVIEW: (id: string) =>
    `/hris/attendance/timesheets/${id}/review`,
  HRIS_LEAVE_TYPES: '/hris/leave/types',
  HRIS_LEAVE_BALANCES: '/hris/leave/balances',
  HRIS_LEAVE_BALANCES_OVERVIEW: '/hris/leave/balances/overview',
  HRIS_LEAVE_REQUESTS: '/hris/leave/requests',
  HRIS_LEAVE_REQUEST_REVIEW: (id: string) =>
    `/hris/leave/requests/${id}/review`,
  HRIS_PAYROLL_RUNS: '/hris/payroll/runs',
  HRIS_PAYROLL_RUN: (id: string) => `/hris/payroll/runs/${id}`,
  HRIS_PAYROLL_RUN_COMPUTE: (id: string) => `/hris/payroll/runs/${id}/compute`,
  HRIS_PAYROLL_RUN_FINALIZE: (id: string) =>
    `/hris/payroll/runs/${id}/finalize`,
  HRIS_PAYROLL_PAYSLIPS: '/hris/payroll/payslips',
  HRIS_SETTINGS: '/hris/settings',
  HRIS_REPORTS_HEADCOUNT: '/hris/reports/headcount',
  HRIS_REPORTS_ATTENDANCE: '/hris/reports/attendance-summary',
  HRIS_REPORTS_LEAVE: '/hris/reports/leave-summary',
  HRIS_REPORTS_PAYROLL_REGISTER: '/hris/reports/payroll-register',
  HRIS_DASHBOARD_OVERVIEW: '/hris/dashboard/overview',
  HRIS_ME_PROFILE: '/hris/me/profile',
  HRIS_ME_PAYSLIPS: '/hris/me/payslips',
  HRIS_ME_LEAVE_BALANCES: '/hris/me/leave-balances',
  HRIS_ME_LEAVE_REQUESTS: '/hris/me/leave-requests',
  HRIS_ME_CLOCK_EVENTS: '/hris/me/clock-events',
  PORTAL_PROFILE: '/portal/profile',
  PORTAL_PAYSLIPS: '/portal/payslips',
  PORTAL_LEAVE_BALANCES: '/portal/leave-balances',
  PORTAL_LEAVE_REQUESTS: '/portal/leave-requests',
  PORTAL_LEAVE_TYPES: '/portal/leave-types',
  PORTAL_ATTENDANCE: '/portal/attendance',
  PORTAL_ATTENDANCE_CLOCK_IN: '/portal/attendance/clock-in',
  PORTAL_ATTENDANCE_CLOCK_OUT: '/portal/attendance/clock-out',
  CAPITALS: '/capitals',
  CAPITAL_BY_ID: (id: string) => `/capitals/${id}`,
  CAPITAL_REPLACE: (id: string) => `/capitals/${id}/replace`,
  CAPITAL_VOID: (id: string) => `/capitals/${id}/void`,
  CASH_VOUCHERS: '/cash-vouchers',
  CASH_VOUCHER_BY_ID: (id: string) => `/cash-vouchers/${id}`,
  CASH_VOUCHER_VOID: (id: string) => `/cash-vouchers/${id}/void`,
  CASH_DISBURSEMENT_JOURNAL: '/cash-disbursements-journal',
  CASH_DISBURSEMENT_2307_PREVIEW: (entryId: string) =>
    `/cash-disbursements-journal/${encodeURIComponent(entryId)}/2307/preview`,
  CASH_DISBURSEMENT_2307_DOWNLOAD: (entryId: string) =>
    `/cash-disbursements-journal/${encodeURIComponent(entryId)}/2307/download`,
  PRODUCT: '/products',
  SHOP: '/shop',
  SHOP_HANDLE: '/shop/handle',
  SHOP_HANDLE_AVAILABILITY: '/shop/handle-availability',
  SHOP_LIVE: '/shop/live',
  PUBLIC_MARKETPLACE: '/public/shop',
  PUBLIC_PRODUCT: (productId: string) =>
    `/public/shop/product/${encodeURIComponent(productId)}`,
  PUBLIC_SHOP: (handle: string) => `/public/shop/${encodeURIComponent(handle)}`,
  PUBLIC_SHOP_ALL_CARTS: '/public/shop-cart',
  PUBLIC_SHOP_ALL_ORDERS: '/public/shop-orders',
  PUBLIC_SHOP_CART: (handle: string) =>
    `/public/shop/${encodeURIComponent(handle)}/cart`,
  PUBLIC_SHOP_CART_ITEM: (handle: string, itemId: string) =>
    `/public/shop/${encodeURIComponent(handle)}/cart/items/${itemId}`,
  PUBLIC_SHOP_ORDERS: (handle: string) =>
    `/public/shop/${encodeURIComponent(handle)}/orders`,
  PUBLIC_SHOP_CHECKOUT: (handle: string) =>
    `/public/shop/${encodeURIComponent(handle)}/orders/checkout`,
  PUBLIC_SHOP_ORDER: (handle: string, orderNumber: string) =>
    `/public/shop/${encodeURIComponent(handle)}/orders/${orderNumber}`,
  PUBLIC_SHOP_ORDER_CANCEL: (handle: string, orderNumber: string) =>
    `/public/shop/${encodeURIComponent(handle)}/orders/${orderNumber}/cancel`,
  SHOP_ORDERS: '/shop/orders',
  SHOP_ORDER_BY_NUMBER: (orderNumber: string) => `/shop/orders/${orderNumber}`,
  SHOP_ORDER_STATUS: (orderNumber: string) =>
    `/shop/orders/${orderNumber}/status`,
  SHOP_ORDER_CANCEL: (orderNumber: string) =>
    `/shop/orders/${orderNumber}/cancel`,
  INSTOREQR_ORDERS: '/instoreqr/orders',
  INSTOREQR_ORDER: (orderId: string) =>
    `/instoreqr/orders/${encodeURIComponent(orderId)}`,
  INSTOREQR_ORDER_STATUS: (orderId: string) =>
    `/instoreqr/orders/${encodeURIComponent(orderId)}/status`,
  INSTOREQR_ORDER_PAYMENT: (orderId: string) =>
    `/instoreqr/orders/${encodeURIComponent(orderId)}/payment`,
  INSTOREQR_ORDERS_PAYMENT_BY_TABLE: '/instoreqr/orders/by-table/payment',
  RESERVE_PLACES: '/reserve/places',
  RESERVE_CONFIG: '/reserve/config',
  RESERVE_HANDLE: '/reserve/handle',
  RESERVE_HANDLE_AVAILABILITY: '/reserve/handle-availability',
  RESERVE_PLACE: (placeId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}`,
  RESERVE_PLACE_IMAGE_UPLOAD_URL: (placeId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}/images/upload-url`,
  RESERVE_PLACE_IMAGES: (placeId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}/images`,
  RESERVE_PLACE_IMAGE_DOWNLOAD_URLS: (placeId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}/images/download-urls`,
  RESERVE_PLACE_AVAILABILITY: (placeId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}/availability`,
  RESERVE_PLACE_BLOCKS: (placeId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}/blocks`,
  RESERVE_PLACE_BLOCK: (placeId: string, blockId: string) =>
    `/reserve/places/${encodeURIComponent(placeId)}/blocks/${encodeURIComponent(blockId)}`,
  RESERVE_RESERVATIONS: '/reserve/reservations',
  RESERVE_AUTHORIZED_PERSONS: '/reserve/authorized-persons',
  RESERVE_AUTHORIZED_PERSON: (personId: string) =>
    `/reserve/authorized-persons/${encodeURIComponent(personId)}`,
  RESERVE_ADD_ONS: '/reserve/add-ons',
  RESERVE_ADD_ON: (addOnId: string) =>
    `/reserve/add-ons/${encodeURIComponent(addOnId)}`,
  RESERVE_ADD_ON_IMAGE_UPLOAD_URL: (addOnId: string) =>
    `/reserve/add-ons/${encodeURIComponent(addOnId)}/image/upload-url`,
  RESERVE_ADD_ON_IMAGE: (addOnId: string) =>
    `/reserve/add-ons/${encodeURIComponent(addOnId)}/image`,
  RESERVE_ADD_ON_IMAGE_DOWNLOAD_URL: (addOnId: string) =>
    `/reserve/add-ons/${encodeURIComponent(addOnId)}/image/download-url`,
  RESERVE_EMAIL_SETTINGS: '/reserve/email-settings',
  RESERVE_PAYMENT_SETTINGS: '/reserve/payment-settings',
  RESERVE_PAYMENT_METHODS: '/reserve/payment-methods',
  RESERVE_PAYMENT_METHOD: (methodId: string) =>
    `/reserve/payment-methods/${methodId}`,
  RESERVE_PAYMENT_METHOD_UPLOAD_URL: (methodId: string) =>
    `/reserve/payment-methods/${methodId}/upload-url`,
  RESERVE_PAYMENT_METHOD_QR: (methodId: string) =>
    `/reserve/payment-methods/${methodId}/qr`,
  RESERVE_PAYMENT_METHOD_DOWNLOAD_URL: (methodId: string) =>
    `/reserve/payment-methods/${methodId}/download-url`,
  RESERVE_PAYMENT_BANKS: '/reserve/payment-banks',
  RESERVE_PAYMENT_BANK: (bankId: string) => `/reserve/payment-banks/${bankId}`,
  RESERVE_PAYMENT_BANK_UPLOAD_URL: (bankId: string) =>
    `/reserve/payment-banks/${bankId}/upload-url`,
  RESERVE_PAYMENT_BANK_LOGO: (bankId: string) =>
    `/reserve/payment-banks/${bankId}/logo`,
  RESERVE_PAYMENT_BANK_DOWNLOAD_URL: (bankId: string) =>
    `/reserve/payment-banks/${bankId}/download-url`,
  RESERVE_RESERVATION_CONFIRM: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}/confirm`,
  RESERVE_RESERVATION_CANCEL: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}/cancel`,
  RESERVE_RESERVATION: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}`,
  RESERVE_RESERVATION_PROOF_DOWNLOAD_URL: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}/proof/download-url`,
  RESERVE_RESERVATION_PROOF_UPLOAD_URL: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}/proof/upload-url`,
  RESERVE_RESERVATION_PROOF: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}/proof`,
  RESERVE_RESERVATION_PAID_TO: (id: string) =>
    `/reserve/reservations/${encodeURIComponent(id)}/paid-to`,
  PUBLIC_RESERVE_PLACES: (handle: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/places`,
  PUBLIC_RESERVE_PLACE: (handle: string, placeId: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/places/${encodeURIComponent(placeId)}`,
  PUBLIC_RESERVE_PLACE_AVAILABILITY: (handle: string, placeId: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/places/${encodeURIComponent(placeId)}/availability`,
  PUBLIC_RESERVE_PLACE_MONTH_AVAILABILITY: (handle: string, placeId: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/places/${encodeURIComponent(placeId)}/month-availability`,
  PUBLIC_RESERVE_PAYMENT_QR: (handle: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/payment-qr`,
  PUBLIC_RESERVE_BOOKINGS: (handle: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/bookings`,
  PUBLIC_RESERVE_BOOKINGS_LOOKUP: (handle: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/bookings/lookup`,
  PUBLIC_RESERVE_BOOKING_PROOF_UPLOAD_URL: (
    handle: string,
    bookingReference: string,
  ) =>
    `/public/reserve/${encodeURIComponent(handle)}/bookings/${encodeURIComponent(bookingReference)}/proof/upload-url`,
  PUBLIC_RESERVE_BOOKING_PROOF: (handle: string, bookingReference: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/bookings/${encodeURIComponent(bookingReference)}/proof`,
  PUBLIC_RESERVE_BOOKING_CONFIRM_CASH_SCAN: (
    handle: string,
    bookingReference: string,
  ) =>
    `/public/reserve/${encodeURIComponent(handle)}/bookings/${encodeURIComponent(bookingReference)}/confirm-cash-scan`,
  PUBLIC_RESERVE_EVENTS_STREAM: (handle: string) =>
    `/public/reserve/${encodeURIComponent(handle)}/events/stream`,
  INSTOREQR_OPERATING_SESSION_ACTIVE: '/instoreqr/operating-session/active',
  INSTOREQR_OPERATING_SESSION: '/instoreqr/operating-session',
  INSTOREQR_OPERATING_SESSION_CLOSE: (sessionId: string) =>
    `/instoreqr/operating-session/${encodeURIComponent(sessionId)}/close`,
  INSTOREQR_CHANNELS: '/instoreqr/channels',
  INSTOREQR_CHANNEL: (channelId: string) =>
    `/instoreqr/channels/${encodeURIComponent(channelId)}`,
  PUBLIC_INSTOREQR_ORDERS: '/public/instoreqr/orders',
  PUBLIC_INSTOREQR_ORDERS_READY_FOR_PAYMENT:
    '/public/instoreqr/orders/ready-for-payment',
  PUBLIC_INSTOREQR_OPERATING_STATUS: '/public/instoreqr/operating-status',
  PUBLIC_INSTOREQR_CHANNELS: '/public/instoreqr/channels',
  PUBLIC_INSTOREQR_PRODUCTS: '/public/instoreqr/products',
  PUBLIC_INSTOREQR_TABLE_VISIT_JOIN: '/public/instoreqr/table-visits/join',
  PUBLIC_INSTOREQR_TABLE_VISIT_ACTIVE: '/public/instoreqr/table-visits/active',
  PUBLIC_INSTOREQR_TABLE_VISIT: (visitId: string) =>
    `/public/instoreqr/table-visits/${encodeURIComponent(visitId)}`,
  PRODUCT_DRAFT: '/products/draft',
  PRODUCT_MENU_IMPORT_UPLOAD_URL: '/products/menu-import/upload-url',
  PRODUCT_MENU_IMPORT_EXTRACT: '/products/menu-import/extract',
  PRODUCT_MENU_IMPORT_CONFIRM: '/products/menu-import/confirm',
  PRODUCT_BY_ID: (id: string) => `/products/${id}`,
  PRODUCT_MEDIA_UPLOAD_URL: (id: string) => `/products/${id}/media/upload-url`,
  PRODUCT_MEDIA: (id: string) => `/products/${id}/media`,
  PRODUCT_MEDIA_DOWNLOAD_URLS: (id: string) =>
    `/products/${id}/media/download-urls`,
  PRODUCT_DRAFT_BY_ID: (id: string) => `/products/draft/${id}`,
  PRODUCT_DRAFT_PUBLISH: (id: string) => `/products/draft/${id}/publish`,
  PRODUCT_UNPUBLISH: (id: string) => `/products/${id}/unpublish`,
  PRODUCT_RESTORE: (id: string) => `/products/${id}/restore`,
  PRODUCT_AUDIT_EVENTS: (id: string) => `/products/${id}/audit-events`,
  PRODUCT_HISTORY: (id: string) => `/products/${id}/history`,
  PRODUCT_ROLLBACK: (id: string, snapshotId: string) =>
    `/products/${id}/rollback/${snapshotId}`,
  POS_REGISTERS: '/pos-registers',
  POS_BOOTSTRAP: '/pos/bootstrap',
  POS_REGISTER_BY_ID: (id: string) => `/pos-registers/${id}`,
  POS_REGISTER_RESTORE: (id: string) => `/pos-registers/${id}/restore`,
  POS_EIS_CATCH_UP_PREVIEW: '/pos-registers/eis/catch-up-preview',
  POS_EIS_CATCH_UP: '/pos-registers/eis/catch-up',
  POS_EIS_CONFIG: '/pos-registers/eis/config',
  POS_EIS_REPORTING: '/pos-registers/eis/reporting',
  POS_EIS_TRANSMISSIONS: '/pos-registers/eis/transmissions',
  POS_EIS_TRANSMISSIONS_EXPORT: '/pos-registers/eis/transmissions/export',
  POS_EIS_TRANSMISSION_BY_ID: (id: string) =>
    `/pos-registers/eis/transmissions/${id}`,
  POS_EIS_TRANSMISSION_PAYLOAD: (id: string) =>
    `/pos-registers/eis/transmissions/${id}/payload`,
  POS_REGISTER_SESSIONS: (registerId: string) =>
    `/pos-registers/${registerId}/sessions`,
  POS_REGISTER_SESSION_ACTIVE: (registerId: string) =>
    `/pos-registers/${registerId}/sessions/active`,
  POS_REGISTER_SESSION_CLOSE: (registerId: string, sessionId: string) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/close`,
  POS_REGISTER_SESSION_X_READING: (registerId: string, sessionId: string) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/x-reading`,
  POS_REGISTER_X_READINGS: (registerId: string) =>
    `/pos-registers/${registerId}/x-readings`,
  POS_REGISTER_X_READING_BY_ID: (registerId: string, xReadingId: string) =>
    `/pos-registers/${registerId}/x-readings/${xReadingId}`,
  POS_REGISTER_Z_READING: (registerId: string) =>
    `/pos-registers/${registerId}/z-reading`,
  POS_REGISTER_Z_READING_SUGGESTED_BUSINESS_DATE: (registerId: string) =>
    `/pos-registers/${registerId}/z-reading/suggested-business-date`,
  POS_REGISTER_Z_READINGS: '/pos-registers/z-readings',
  POS_REGISTER_Z_READING_BY_ID: (registerId: string, zReadingId: string) =>
    `/pos-registers/${registerId}/z-reading/${zReadingId}`,
  POS_REGISTER_Z_READING_PRINT: (registerId: string, zReadingId: string) =>
    `/pos-registers/${registerId}/z-reading/${zReadingId}/print`,
  POS_REGISTER_E_JOURNAL: (registerId: string) =>
    `/pos-registers/${registerId}/e-journal`,
  POS_REGISTER_E_JOURNAL_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/e-journal/export`,
  POS_REGISTER_BIR_REPORTS: (registerId: string) =>
    `/pos-registers/${registerId}/bir-reports`,
  POS_REGISTER_BIR_REPORTS_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/bir-reports/export`,
  POS_REGISTER_BIR_SALES_SUMMARY: (registerId: string) =>
    `/pos-registers/${registerId}/bir-reports/sales-summary`,
  POS_REGISTER_BIR_SALES_SUMMARY_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/bir-reports/sales-summary/export`,
  POS_REGISTER_DAILY_CASH_REPORT: (registerId: string) =>
    `/pos-registers/${registerId}/daily-cash-report`,
  POS_REGISTER_DAILY_CASH_REPORT_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/daily-cash-report/export`,
  POS_REGISTER_TERMINAL_ACCOUNTABILITY_REPORT: (registerId: string) =>
    `/pos-registers/${registerId}/terminal-accountability-report`,
  POS_REGISTER_TERMINAL_ACCOUNTABILITY_REPORT_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/terminal-accountability-report/export`,
  POS_REGISTER_DETAILED_SALES_REPORT: (registerId: string) =>
    `/pos-registers/${registerId}/detailed-sales-report`,
  POS_REGISTER_DETAILED_SALES_REPORT_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/detailed-sales-report/export`,
  POS_REGISTER_ITEMS_SOLD_LEDGER: (registerId: string) =>
    `/pos-registers/${registerId}/items-sold-ledger`,
  POS_REGISTER_ITEMS_SOLD_LEDGER_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/items-sold-ledger/export`,
  POS_SALES_GENERAL_LEDGER: '/pos-sales-general-ledger',
  POS_SALES_GENERAL_LEDGER_POST: '/pos-sales-general-ledger/post',
  POS_INVENTORY_GENERAL_LEDGER: '/pos-inventory-general-ledger',
  POS_INVENTORY_GENERAL_LEDGER_POST: '/pos-inventory-general-ledger/post',
  POS_REGISTER_VOID_REPORT: (registerId: string) =>
    `/pos-registers/${registerId}/void-report`,
  POS_REGISTER_VOID_REPORT_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/void-report/export`,
  POS_REGISTER_REFUND_REPORT: (registerId: string) =>
    `/pos-registers/${registerId}/refund-report`,
  POS_REGISTER_REFUND_REPORT_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/refund-report/export`,
  POS_REGISTER_SC_SALES_BOOK: (registerId: string) =>
    `/pos-registers/${registerId}/senior-citizen-sales-book`,
  POS_REGISTER_SC_SALES_BOOK_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/senior-citizen-sales-book/export`,
  POS_REGISTER_PWD_SALES_BOOK: (registerId: string) =>
    `/pos-registers/${registerId}/pwd-sales-book`,
  POS_REGISTER_PWD_SALES_BOOK_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/pwd-sales-book/export`,
  POS_REGISTER_NAAC_SALES_BOOK: (registerId: string) =>
    `/pos-registers/${registerId}/naac-sales-book`,
  POS_REGISTER_NAAC_SALES_BOOK_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/naac-sales-book/export`,
  POS_REGISTER_SOLO_PARENT_SALES_BOOK: (registerId: string) =>
    `/pos-registers/${registerId}/solo-parent-sales-book`,
  POS_REGISTER_SOLO_PARENT_SALES_BOOK_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/solo-parent-sales-book/export`,
  POS_REGISTER_ACTIVITY_LOG: (registerId: string) =>
    `/pos-registers/${registerId}/activity-log`,
  POS_REGISTER_ACTIVITY_LOG_EXPORT: (registerId: string) =>
    `/pos-registers/${registerId}/activity-log/export`,
  POS_REGISTER_SESSION_MINE: '/pos-registers/sessions/mine',
  POS_REGISTER_SESSION_SALES: (registerId: string, sessionId: string) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/sales`,
  POS_REGISTER_SESSION_SALE_BY_ID: (
    registerId: string,
    sessionId: string,
    saleId: string,
  ) => `/pos-registers/${registerId}/sessions/${sessionId}/sales/${saleId}`,
  POS_REGISTER_SESSION_SALE_VOID: (
    registerId: string,
    sessionId: string,
    saleId: string,
  ) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/sales/${saleId}/void`,
  POS_REGISTER_SESSION_SALE_REFUND: (
    registerId: string,
    sessionId: string,
    saleId: string,
  ) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/sales/${saleId}/refund`,
  POS_REGISTER_SESSION_SALE_REPRINT: (
    registerId: string,
    sessionId: string,
    saleId: string,
  ) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/sales/${saleId}/reprint`,
  POS_REGISTER_SESSION_SALE_EXPORT_EIS: (
    registerId: string,
    sessionId: string,
    saleId: string,
  ) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/sales/${saleId}/export-eis`,
  POS_REGISTER_SESSION_SALE_EIS_EXPORT: (
    registerId: string,
    sessionId: string,
    saleId: string,
  ) =>
    `/pos-registers/${registerId}/sessions/${sessionId}/sales/${saleId}/eis-export`,
  /** Public (no auth): POS receipt authenticity by company + verification seal. */
  PUBLIC_POS_RECEIPT_VERIFY: (companyId: string, seal: string) =>
    `/public/pos/receipts/verify/${encodeURIComponent(companyId.trim())}/${encodeURIComponent(seal.trim())}`,
  INVENTORY_V2: '/inventory-v2',
  INVENTORY_V2_BY_SUPPLIER: (supplierId: string) =>
    `/inventory-v2/by-supplier/${supplierId}`,
  INVENTORY_V2_LOTS: '/inventory-v2/lots',
  INVENTORY_V2_COMPANY_MOVEMENTS: '/inventory-v2/movements',
  INVENTORY_V2_MOVEMENTS: (productId: string) =>
    `/inventory-v2/movements/${productId}`,
  INVENTORY_V2_PURCHASE_ORDERS: '/inventory-v2/purchase-orders',
  INVENTORY_V2_PURCHASE_ORDER_BY_ID: (id: string) =>
    `/inventory-v2/purchase-orders/${id}`,
  INVENTORY_V2_PURCHASE_ORDER_VOID: (id: string) =>
    `/inventory-v2/purchase-orders/${id}/void`,
  INVENTORY_V2_RECEIVES: '/inventory-v2/receives',
  INVENTORY_V2_RECEIVE_BY_ID: (id: string) => `/inventory-v2/receives/${id}`,
  INVENTORY_V2_RECEIVE_POST: (id: string) =>
    `/inventory-v2/receives/${id}/post`,
  INVENTORY_V2_RECEIVE_VOID: (id: string) =>
    `/inventory-v2/receives/${id}/void`,
  INVENTORY_V2_TRANSFERS: '/inventory-v2/transfers',
  INVENTORY_V2_TRANSFER_BY_ID: (id: string) => `/inventory-v2/transfers/${id}`,
  INVENTORY_V2_TRANSFER_SHIP: (id: string) =>
    `/inventory-v2/transfers/${id}/ship`,
  INVENTORY_V2_TRANSFER_RECEIVE: (id: string) =>
    `/inventory-v2/transfers/${id}/receive`,
  INVENTORY_V2_TRANSFER_RETURN_REMAINDER: (id: string) =>
    `/inventory-v2/transfers/${id}/return-remainder`,
  INVENTORY_V2_TRANSFER_CANCEL: (id: string) =>
    `/inventory-v2/transfers/${id}/cancel`,
  INVENTORY_V2_STOCK_COUNTS: '/inventory-v2/stock-counts',
  INVENTORY_V2_STOCK_COUNT_BY_ID: (id: string) =>
    `/inventory-v2/stock-counts/${id}`,
  INVENTORY_V2_STOCK_COUNT_COMPLETE: (id: string) =>
    `/inventory-v2/stock-counts/${id}/complete`,
  INVENTORY_V2_REORDER_RULES: '/inventory-v2/reorder-rules',
  INVENTORY_V2_REORDER_RULE_BY_ID: (id: string) =>
    `/inventory-v2/reorder-rules/${id}`,
  INVENTORY_V2_LOW_STOCK: '/inventory-v2/reorder-rules/low-stock',
  INVENTORY_V2_IMPORT_DRY_RUN: '/inventory-v2/imports/dry-run',
  INVENTORY_V2_IMPORT_COMMIT: '/inventory-v2/imports/commit',
  POS_WAREHOUSES: '/pos-warehouses',
  POS_WAREHOUSE_BY_ID: (id: string) => `/pos-warehouses/${id}`,
  POS_WAREHOUSE_SET_DEFAULT: (id: string) =>
    `/pos-warehouses/${id}/set-default`,
  POS_WAREHOUSE_ARCHIVE: (id: string) => `/pos-warehouses/${id}/archive`,
  POS_WAREHOUSE_BINS: (warehouseId: string) =>
    `/pos-warehouses/${warehouseId}/bins`,
  POS_WAREHOUSE_BIN_BY_ID: (binId: string) => `/pos-warehouses/bins/${binId}`,
  POS_WAREHOUSE_BIN_ARCHIVE: (binId: string) =>
    `/pos-warehouses/bins/${binId}/archive`,
  DONATIONS: {
    CREATE: '/donations',
    CONFIRM: '/donations/confirm',
    STATUS: '/donations/:id/status',
  },
  REPORTS: {
    INCOME_STATEMENT: '/reports/income-statement',
    /** GET: compare transaction vs projection income statement totals */
    INCOME_STATEMENT_PARITY_CHECK: '/reports/income-statement/parity-check',
    BALANCE_SHEET: '/reports/balance-sheet',
    CASH_FLOW_STATEMENT: '/reports/cash-flow-statement',
    STATEMENT_OF_CHANGES_IN_EQUITY: '/reports/statement-of-changes-in-equity',
    FINANCIAL_STATEMENT: '/reports/financial-statement',
    GENERAL_LEDGER: '/reports/general-ledger',
    LEDGER_PROJECTION: '/reports/ledger-projection',
    /** GET: per-category projection balances as of a date */
    LEDGER_PROJECTION_BALANCES_AS_OF:
      '/reports/ledger-projection/balances-as-of',
    /** GET: per-period category rollups from materialized projection (day|month) */
    LEDGER_PROJECTION_ROLLUPS: '/reports/ledger-projection/rollups',
    /** GET: daily/monthly projection totals aggregated from category rollups */
    LEDGER_PROJECTION_ROLLUP_TOTALS: '/reports/ledger-projection/rollup-totals',
    /** GET: projection queue metrics and threshold-based alerts */
    LEDGER_PROJECTION_OBSERVABILITY: '/reports/ledger-projection/observability',
    /** POST: enqueue reconciliation re-projection for current company */
    LEDGER_PROJECTION_RECONCILE: '/reports/ledger-projection/reconcile',
    GENERAL_LEDGER_EXPORT: '/reports/general-ledger/export',
    TRIAL_BALANCE: '/reports/trial-balance',
    GENERAL_JOURNAL: '/reports/general-journal',
    HEADER_SETTINGS: '/reports/header-settings',
    FINANCIAL_STATEMENT_CONTEXT: '/reports/financial-statement/context',
    FINANCIAL_STATEMENT_SETTINGS: '/reports/financial-statement/settings',
  },
  TAX_COMPLIANCE: {
    DASHBOARD: '/tax-compliance/dashboard',
    ALL_COMPANIES_TAX_REMINDERS:
      '/tax-compliance/dashboard/all-companies-tax-reminders',
    CATALOG: '/tax-compliance/catalog',
    SETUP: '/tax-compliance/setup',
    SETUP_FORM_TYPE_YEARS: '/tax-compliance/setup/form-type-years',
    GENERATE_DEADLINES: '/tax-compliance/generate-deadlines',
    CLEANUP_0619E_QUARTER_END: '/tax-compliance/cleanup-0619e-quarter-end',
    REMOVE_FORMS: '/tax-compliance/remove-forms',
    DISMISS_OBLIGATIONS: '/tax-compliance/dismiss-obligations',
    RESTORE_OBLIGATIONS: '/tax-compliance/restore-obligations',
    OBLIGATIONS: '/tax-compliance/obligations',
    OBLIGATIONS_UPDATE_STATUSES: '/tax-compliance/obligations/update-statuses',
    OBLIGATIONS_UPDATE_TAGS: '/tax-compliance/obligations/update-tags',
    COR_UPLOAD_URL: '/tax-compliance/cor/upload-url',
    COR: '/tax-compliance/cor',
    COR_APPLY_DETECTED: '/tax-compliance/cor/apply-detected',
    COR_REEVALUATE: '/tax-compliance/cor/reevaluate',
    COR_DOWNLOAD_URL: '/tax-compliance/cor/download-url',
    COR_PREVIEW_URL: '/tax-compliance/cor/preview-url',
    QAP_1601EQ_PREVIEW: '/tax-compliance/qap/1601eq/preview',
    QAP_1601EQ_DOWNLOAD: '/tax-compliance/qap/1601eq/download',
    OBLIGATION_PROOF_UPLOAD_URL: (formCode: string, periodKey: string) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/proof/upload-url`,
    OBLIGATION_PROOF_ATTACH: (formCode: string, periodKey: string) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/proof`,
    OBLIGATION_PROOF_DOWNLOAD_URL: (formCode: string, periodKey: string) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/proof/download-url`,
    OBLIGATION_FILING_PROGRESS: (formCode: string, periodKey: string) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/filing-progress`,
    OBLIGATION_FILING_PROGRESS_PROOF_UPLOAD_URL: (
      formCode: string,
      periodKey: string,
      kind: 'bir' | 'trrc' | 'payment',
    ) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/filing-progress/${kind}/proof/upload-url`,
    OBLIGATION_FILING_PROGRESS_PROOF_ATTACH: (
      formCode: string,
      periodKey: string,
      kind: 'bir' | 'trrc' | 'payment',
    ) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/filing-progress/${kind}/proof`,
    OBLIGATION_FILING_MERGED_PDF: (formCode: string, periodKey: string) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/filing-files/merged-pdf`,
    OBLIGATION_FILING_PROGRESS_ARCHIVE: (
      formCode: string,
      periodKey: string,
      kind: 'bir' | 'trrc' | 'payment',
    ) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/filing-progress/${kind}/archive`,
    OBLIGATION_FILING_PROGRESS_RESTORE: (
      formCode: string,
      periodKey: string,
      kind: 'bir' | 'trrc' | 'payment',
    ) =>
      `/tax-compliance/obligations/${encodeURIComponent(formCode)}/${encodeURIComponent(periodKey)}/filing-progress/${kind}/restore`,
  },
  BUSINESS_CALENDAR: {
    DASHBOARD: '/business-calendar/dashboard',
    DOCUMENT_TYPES: '/business-calendar/document-types',
    DOCUMENTS_UPLOAD_URL: '/business-calendar/documents/upload-url',
    DOCUMENTS: '/business-calendar/documents',
    DOCUMENT: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}`,
    DOCUMENT_CONFIRM: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/confirm`,
    DOCUMENT_RENEW: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/renew`,
    DOCUMENT_ATTACHMENT: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/attachment`,
    DOCUMENT_FILED: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/filed`,
    DOCUMENT_REMINDER: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/reminder`,
    DOCUMENT_DOWNLOAD_URL: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/download-url`,
    DOCUMENT_PREVIEW_URL: (id: string) =>
      `/business-calendar/documents/${encodeURIComponent(id)}/preview-url`,
    EMPLOYEE_REMINDERS: '/business-calendar/employee-reminders',
    EMPLOYEE_REMINDER: (id: string) =>
      `/business-calendar/employee-reminders/${encodeURIComponent(id)}`,
  },
  FILE_MANAGER: {
    ROOT: '/file-manager',
    FOLDERS: '/file-manager/folders',
    FOLDER: (id: string) => `/file-manager/folders/${encodeURIComponent(id)}`,
    FOLDER_MOVE: (id: string) =>
      `/file-manager/folders/${encodeURIComponent(id)}/move`,
    FILES_UPLOAD_URLS: '/file-manager/files/upload-urls',
    FILES_REGISTER: '/file-manager/files/register',
    FILE_DOWNLOAD_URL: (id: string) =>
      `/file-manager/files/${encodeURIComponent(id)}/download-url`,
    FILE: (id: string) => `/file-manager/files/${encodeURIComponent(id)}`,
    FILE_MOVE: (id: string) =>
      `/file-manager/files/${encodeURIComponent(id)}/move`,
  },
  EVENTS: {
    ROOT: '/events',
    IMPORT_GOOGLE_FORM_PREVIEW: '/events/import/google-form/preview',
    DETAIL: (eventId: string) => `/events/${encodeURIComponent(eventId)}`,
    CHANNELS: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/channels`,
    CHANNEL: (eventId: string, channelId: string) =>
      `/events/${encodeURIComponent(eventId)}/channels/${encodeURIComponent(channelId)}`,
    SCANNER_LINKS: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/scanner-links`,
    SCANNER_LINK: (eventId: string, scannerLinkId: string) =>
      `/events/${encodeURIComponent(eventId)}/scanner-links/${encodeURIComponent(scannerLinkId)}`,
    REGISTRATIONS: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations`,
    REGISTRATIONS_IMPORT: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/import`,
    REGISTRATIONS_IMPORT_MAPPING_SUGGESTIONS: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/import/mapping-suggestions`,
    REGISTRATIONS_EMAIL_BLAST: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/email-blast`,
    REGISTRATION_RESEND_TICKET: (eventId: string, registrationId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(registrationId)}/resend-ticket`,
    REGISTRATION_ARCHIVE: (eventId: string, registrationId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(registrationId)}/archive`,
    REGISTRATION_RESTORE: (eventId: string, registrationId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(registrationId)}/restore`,
    REGISTRATIONS_ARCHIVE: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/archive`,
    REGISTRATIONS_RESTORE: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/registrations/restore`,
    WALK_INS: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/walk-ins`,
    ANALYTICS: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/analytics`,
    SURVEY_RESPONSES: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/survey-responses`,
    BANNER_UPLOAD_URL: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/banner/upload-url`,
    BANNER: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/banner`,
    BANNER_DOWNLOAD_URL: (eventId: string) =>
      `/events/${encodeURIComponent(eventId)}/banner/download-url`,
    PUBLIC_REGISTER: (companyId: string, channelToken: string) =>
      `/public/events/register/${encodeURIComponent(companyId)}/${encodeURIComponent(channelToken)}`,
    PUBLIC_TICKET: (ticketToken: string) =>
      `/public/events/ticket/${encodeURIComponent(ticketToken)}`,
    PUBLIC_SCANNER_AUTH: (scannerToken: string) =>
      `/public/events/scanner/${encodeURIComponent(scannerToken)}/auth`,
    PUBLIC_SCANNER_CHECK_IN: (scannerToken: string) =>
      `/public/events/scanner/${encodeURIComponent(scannerToken)}/check-in`,
    PUBLIC_SURVEY: '/public/events/survey',
    PUBLIC_SURVEY_PAGE: (ticketToken: string) =>
      `/public/events/survey/${encodeURIComponent(ticketToken)}`,
  },
  ACTIVITY_LOGS: '/activity-logs',
  ACTIVITY_LOGS_ACTION_CATEGORIES: '/activity-logs/action-categories',
  NOTIFICATIONS: '/notifications',
  NOTIFICATIONS_UNREAD_COUNT: '/notifications/unread-count',
  NOTIFICATIONS_MARK_ALL_READ: '/notifications/read-all',
  NOTIFICATIONS_AUDIT_VIEW_ALL: '/notifications/audit/view-all',
  NOTIFICATION: (notificationId: string) => `/notifications/${notificationId}`,
  NOTIFICATION_MARK_READ: (notificationId: string) =>
    `/notifications/${notificationId}/read`,
  COMPANY_USER_INVITATION_PREVIEW: (invitationId: string) =>
    `/company/user-invitations/${invitationId}/preview`,
  COMPANY_USER_INVITATION_ACCEPT: (invitationId: string) =>
    `/company/user-invitations/${invitationId}/accept`,
  COMPANY_USER_INVITATION_DECLINE: (invitationId: string) =>
    `/company/user-invitations/${invitationId}/decline`,
  PAYMONGO_ADMIN: {
    CONNECTION: '/paymongo/admin/connection',
    CHECKOUT_CONFIG: '/paymongo/admin/checkout-config',
    ACTIVITY_LOGS: '/paymongo/admin/activity-logs',
  },
  SUBSCRIPTIONS: {
    PLANS: '/subscriptions/plans',
    ME: '/subscriptions/me',
    PURCHASE_HISTORY: '/subscriptions/purchase-history',
    ENTITLEMENTS: '/subscriptions/entitlements',
    PROVIDER_STATUS: '/subscriptions/provider-status',
    CHECKOUT_SESSIONS: '/subscriptions/checkout-sessions',
    CANCEL: '/subscriptions/cancel',
  },
  SUBSCRIPTIONS_ADMIN: {
    PLANS: '/subscriptions/admin/plans',
    PLAN: (id: string) =>
      `/subscriptions/admin/plans/${encodeURIComponent(id)}`,
    ACTIVITY_LOGS: '/subscriptions/admin/activity-logs',
  },
};

/** Active company for multi-tenant API calls; must match backend `x-company-id`. */
export const COMPANY_ID_HEADER = 'x-company-id' as const;

// UI Routes
export const ROUTES = {
  HOME: '/',
  /** Public income tax regime comparison (no login). */
  TAX_CALCULATOR: '/tax-calculator',
  /** Full-tab receipt image on hub origin; query `token` for API view URL. */
  RECEIPT_IMAGE_TAB: '/receipt-image',
  /** Public POS receipt authenticity page (short QR / barcode / printed URL). */
  VERIFY_RECEIPT_PATH: '/v/r/:companyId/:seal',
  VERIFY_RECEIPT: (companyId: string, seal: string) =>
    `/v/r/${encodeURIComponent(companyId.trim())}/${encodeURIComponent(seal.trim())}`,
  /** Public AR invoice authenticity page (printed URL / QR). */
  VERIFY_AR_INVOICE_PATH: '/v/i/:companyId/:seal',
  VERIFY_AR_INVOICE: (companyId: string, seal: string) =>
    `/v/i/${encodeURIComponent(companyId.trim())}/${encodeURIComponent(seal.trim())}`,
  /** Static gate + SPA fallback; must stay in sync with `public/coming-soon.html` and `index.html`. */
  COMING_SOON_HTML: '/coming-soon.html',
  DASHBOARD: '/dashboard',
  HEALTH_CHECK: '/health-check',
  SUPER_DASHBOARD: '/super-dashboard',
  SUPER_DASHBOARD_HEALTH_CHECKS: '/super-dashboard/health-checks',
  SUPER_DASHBOARD_TAX_REMINDERS: '/super-dashboard/tax-reminders',
  SUPER_DASHBOARD_UPLOAD_ENTRIES: '/super-dashboard/upload-entries',
  SUPER_DASHBOARD_UPLOADED_ENTRIES_EMAIL:
    '/super-dashboard/uploaded-entries-email',
  SUPER_DASHBOARD_ACTIVITY_LOGS: '/super-dashboard/activity-logs',
  TAX_COMPLIANCE: '/tax-compliance',
  QAP_1601EQ: '/tax-compliance/qap/1601eq',
  BUSINESS_CALENDAR: '/business-calendar',
  BUSINESS_CALENDAR_ACTION_ITEMS: '/business-calendar/action-items',
  FILE_MANAGER: '/file-manager',
  EVENTS: {
    ROOT: '/events',
    NEW: '/events/new',
    VIEW: '/events/:eventId',
    EDIT: '/events/:eventId/edit',
    DETAIL: (eventId: string) => `/events/${eventId}`,
    EDIT_PATH: (eventId: string) => `/events/${eventId}/edit`,
    PUBLIC_REGISTER: '/events/register/:companyId/:channelToken',
    PUBLIC_REGISTER_PATH: (companyId: string, channelToken: string) =>
      `/events/register/${encodeURIComponent(companyId)}/${encodeURIComponent(channelToken)}`,
    PUBLIC_TICKET: '/events/ticket/:ticketToken',
    PUBLIC_TICKET_PATH: (ticketToken: string) =>
      `/events/ticket/${encodeURIComponent(ticketToken)}`,
    PUBLIC_SCANNER: '/events/scanner/:scannerToken',
    PUBLIC_SCANNER_PATH: (scannerToken: string) =>
      `/events/scanner/${encodeURIComponent(scannerToken)}`,
    PUBLIC_SURVEY: '/events/survey/:ticketToken',
    PUBLIC_SURVEY_PATH: (ticketToken: string) =>
      `/events/survey/${encodeURIComponent(ticketToken)}`,
  },
  ACTIVITY_LOGS: '/activity-logs',
  REQUESTS: '/requests',
  REQUEST_DETAIL: (requestId: string) => `/requests/${requestId}`,
  /** Legacy Cash Books route; redirects to the Journals hub. */
  NON_VAT_BOOKS: '/non-vat-books',
  APPS_STORE: {
    ROOT: '/apps-store',
    DETAIL: '/apps-store/:appId',
    DETAIL_PATH: (appId: string) => `/apps-store/${encodeURIComponent(appId)}`,
  },
  INSTOREQR: {
    ROOT: '/instoreqr',
    CHANNELS: '/instoreqr/channel',
    TABLES: '/instoreqr/tables',
    ORDER_MENU: '/instoreqr/order-menu',
    KDS: '/instoreqr/kds',
    SALES_REPORTS: '/instoreqr/sales-reports',
    MENU_IMPORT: '/instoreqr/menu-import',
    MENU: '/instoreqr/menu',
    MENU_UID: '/instoreqr/menu/:uid',
    MENU_PATH: (uid: string) => `/instoreqr/menu/${encodeURIComponent(uid)}`,
  },
  RESERVE: {
    ROOT: '/reserve',
    PLACES_NEW: '/reserve/places/new',
    PLACE_DETAIL: '/reserve/places/:placeId',
    PLACE_DETAIL_PATH: (placeId: string) =>
      `/reserve/places/${encodeURIComponent(placeId)}`,
    PLACE_BLOCKS: '/reserve/places/:placeId/blocks',
    PLACE_BLOCKS_PATH: (placeId: string) =>
      `/reserve/places/${encodeURIComponent(placeId)}/blocks`,
    RESERVATIONS: '/reserve/reservations',
    EMAIL: '/reserve/email',
    PAYMENT: '/reserve/payment',
    CALENDAR: '/reserve/calendar',
    CALENDAR_PLACE: '/reserve/calendar/:placeId',
    CALENDAR_PLACE_PATH: (placeId: string) =>
      `/reserve/calendar/${encodeURIComponent(placeId)}`,
    AUTHORIZE: '/reserve/authorize',
    ADD_ONS: '/reserve/add-ons',
  },
  PUBLIC_RESERVE: {
    ROOT: '/public/reserve/:handle',
    ROOT_PATH: (handle: string) =>
      `/public/reserve/${encodeURIComponent(handle)}`,
    /** Loopback-only camera helper (opened as popup from LAN HTTP pages). */
    CAMERA_CAPTURE: '/public/reserve/camera-capture',
    /** Loopback QR scan helper for authorized-person confirm on LAN HTTP. */
    AUTHORIZED_SCAN_CAPTURE: '/public/reserve/authorized-scan-capture',
    PLACE: '/public/reserve/:handle/places/:placeId',
    PLACE_PATH: (handle: string, placeId: string) =>
      `/public/reserve/${encodeURIComponent(handle)}/places/${encodeURIComponent(placeId)}`,
    BOOK: '/public/reserve/:handle/places/:placeId/book',
    BOOK_PATH: (handle: string, placeId: string) =>
      `/public/reserve/${encodeURIComponent(handle)}/places/${encodeURIComponent(placeId)}/book`,
    CONFIRMATION: '/public/reserve/:handle/confirmation/:bookingReference',
    CONFIRMATION_PATH: (handle: string, bookingReference: string) =>
      `/public/reserve/${encodeURIComponent(handle)}/confirmation/${encodeURIComponent(bookingReference)}`,
    LOOKUP: '/public/reserve/:handle/lookup',
    LOOKUP_PATH: (handle: string) =>
      `/public/reserve/${encodeURIComponent(handle)}/lookup`,
  },
  NOTIFICATIONS: '/notifications',
  NOTIFICATION_DETAIL_PATH: '/notifications/:notificationId',
  NOTIFICATION_DETAIL: (notificationId: string) =>
    `/notifications/${notificationId}`,
  USER_INVITE_PATH: '/invite/:invitationId',
  USER_INVITE: (invitationId: string) => `/invite/${invitationId}`,
  UPLOAD_ENTRY: '/upload-entry',
  UPLOAD_ENTRY_PROCESS: '/upload-entry/process',
  AUTH_CALLBACK: '/auth/callback',
  CATEGORIES: {
    ROOT: '/categories',
  },
  /** Cash disbursement journal — manual CV# log (own layout + `cashDisbursementJournalStore`). */
  CASH_DISBURSEMENT_JOURNAL: {
    ROOT: '/expenses',
    NEW: '/expenses/new',
    VIEW: '/expenses/:entryId',
    EDIT: '/expenses/:entryId/edit',
    ISSUE_2307: '/expenses/:entryId/issue-2307',
    ISSUE_2307_PATH: (entryId: string) =>
      `/expenses/${encodeURIComponent(entryId)}/issue-2307`,
  },
  /** Cash receipts journal — manual OR# log (own layout + `cashReceiptsJournalStore`). */
  CASH_RECEIPTS_JOURNAL: {
    ROOT: '/sales',
    NEW: '/sales/new',
    VIEW: '/sales/:receiptId',
    EDIT: '/sales/:receiptId/edit',
  },
  JOURNALS: {
    ROOT: '/journals',
    CUSTOMERS: '/journals/customers',
    CUSTOMER_EMAIL: '/journals/customers/email',
    CUSTOMER_EMAIL_RECENT_SENDS: '/journals/customers/email/recent-sends',
    BILL_INVOICE: '/journals/customers/bill-invoice',
    SUPPLIERS: '/journals/suppliers',
  },
  TRANSACTIONS: {
    ROOT: '/transactions',
    ADD: '/transactions/add',
    VIEW: '/transactions/:id',
    EDIT: '/transactions/:id/edit',
  },
  BILLS_LEDGER: {
    ROOT: '/accounts-payable/bills',
    NEW: '/accounts-payable/bills/new',
    /** @deprecated Redirects to BILL_PAYMENTS.ROOT */
    PAYMENT_HISTORY: '/accounts-payable/bills/payment-history',
    VIEW: '/accounts-payable/bills/:billId',
    DETAIL: (billId: string) => `/accounts-payable/bills/${billId}`,
  },
  BILL_PAYMENTS: {
    ROOT: '/accounts-payable/bill-payments',
    VIEW: '/accounts-payable/bill-payments/:paymentId',
    DETAIL: (paymentId: string) =>
      `/accounts-payable/bill-payments/${paymentId}`,
  },
  FX_REVALUATIONS: {
    ROOT: '/accounts-payable/fx-revaluations',
    VIEW: '/accounts-payable/fx-revaluations/:entryId',
    DETAIL: (entryId: string) => `/accounts-payable/fx-revaluations/${entryId}`,
  },
  AR_INVOICES: {
    ROOT: '/accounts-receivable/invoices',
    NEW: '/accounts-receivable/invoices/new',
    VIEW: '/accounts-receivable/invoices/:invoiceId',
    DETAIL: (invoiceId: string) => `/accounts-receivable/invoices/${invoiceId}`,
  },
  INVOICE_PAYMENTS: {
    ROOT: '/accounts-receivable/invoice-payments',
    VIEW: '/accounts-receivable/invoice-payments/:paymentId',
    DETAIL: (paymentId: string) =>
      `/accounts-receivable/invoice-payments/${paymentId}`,
  },
  POS: {
    ROOT: '/pos',
    REGISTERS_SETUP: '/pos/registers/setup',
    /** @deprecated Redirects to POS hub — catalog is auto-created on first visit. */
    SETUP: '/pos/setup',
    PRODUCTS: '/pos/products',
    /** @deprecated Redirects to POS hub — catalog is auto-created on first visit. */
    PRODUCTS_SETUP: '/pos/products/setup',
    PRODUCTS_NEW: '/pos/products/new',
    PRODUCTS_EDIT: '/pos/products/:productId/edit',
    PRODUCTS_EDIT_PATH: (productId: string) =>
      `/pos/products/${productId}/edit`,
    INVENTORY: '/pos/inventory',
    INVENTORY_HISTORY: '/pos/inventory/:productId/history',
    INVENTORY_HISTORY_PATH: (
      productId: string,
      productVariantId?: string | null,
    ) => {
      const base = `/pos/inventory/${productId}/history`;
      if (productVariantId) {
        return `${base}?productVariantId=${encodeURIComponent(productVariantId)}`;
      }
      return base;
    },
    WAREHOUSES: '/pos/warehouses',
    WAREHOUSE_BINS: '/pos/warehouses/:warehouseId/bins',
    WAREHOUSE_BINS_PATH: (warehouseId: string) =>
      `/pos/warehouses/${warehouseId}/bins`,
    REORDER_RULES: '/pos/reorder-rules',
    /** Unified Orders & Receiving list and nested PO/receive screens. */
    ORDERS_RECEIVING: '/pos/orders-receiving',
    ORDERS_RECEIVING_PURCHASE_ORDERS_NEW:
      '/pos/orders-receiving/purchase-orders/new',
    ORDERS_RECEIVING_PURCHASE_ORDERS_DETAIL:
      '/pos/orders-receiving/purchase-orders/:purchaseOrderId',
    ORDERS_RECEIVING_PURCHASE_ORDERS_DETAIL_PATH: (purchaseOrderId: string) =>
      `/pos/orders-receiving/purchase-orders/${purchaseOrderId}`,
    ORDERS_RECEIVING_RECEIVES_NEW: '/pos/orders-receiving/receives/new',
    ORDERS_RECEIVING_RECEIVES_DETAIL:
      '/pos/orders-receiving/receives/:receiveId',
    ORDERS_RECEIVING_RECEIVES_DETAIL_PATH: (receiveId: string) =>
      `/pos/orders-receiving/receives/${receiveId}`,
    /** @deprecated Redirects to ORDERS_RECEIVING — kept for bookmarks */
    PURCHASE_ORDERS: '/pos/purchase-orders',
    /** @deprecated Redirects to ORDERS_RECEIVING_PURCHASE_ORDERS_NEW */
    PURCHASE_ORDERS_NEW: '/pos/purchase-orders/new',
    /** @deprecated Redirects to ORDERS_RECEIVING_PURCHASE_ORDERS_DETAIL */
    PURCHASE_ORDERS_DETAIL: '/pos/purchase-orders/:purchaseOrderId',
    /** @deprecated Prefer ORDERS_RECEIVING_PURCHASE_ORDERS_DETAIL_PATH */
    PURCHASE_ORDERS_DETAIL_PATH: (purchaseOrderId: string) =>
      `/pos/purchase-orders/${purchaseOrderId}`,
    /** @deprecated Redirects to ORDERS_RECEIVING — kept for bookmarks */
    RECEIVES: '/pos/receives',
    /** @deprecated Redirects to ORDERS_RECEIVING_RECEIVES_NEW */
    RECEIVES_NEW: '/pos/receives/new',
    /** @deprecated Redirects to ORDERS_RECEIVING_RECEIVES_DETAIL */
    RECEIVES_DETAIL: '/pos/receives/:receiveId',
    /** @deprecated Prefer ORDERS_RECEIVING_RECEIVES_DETAIL_PATH */
    RECEIVES_DETAIL_PATH: (receiveId: string) => `/pos/receives/${receiveId}`,
    TRANSFERS: '/pos/transfers',
    TRANSFERS_NEW: '/pos/transfers/new',
    TRANSFERS_DETAIL: '/pos/transfers/:transferId',
    TRANSFERS_DETAIL_PATH: (transferId: string) =>
      `/pos/transfers/${transferId}`,
    STOCK_COUNTS: '/pos/stock-counts',
    STOCK_COUNTS_NEW: '/pos/stock-counts/new',
    STOCK_COUNTS_DETAIL: '/pos/stock-counts/:stockCountId',
    STOCK_COUNTS_DETAIL_PATH: (stockCountId: string) =>
      `/pos/stock-counts/${stockCountId}`,
    /** @deprecated Redirects to inventory ?lowStock=1 */
    LOW_STOCK: '/pos/low-stock',
    IMPORT: '/pos/import',
    INVENTORY_REPORTS: '/pos/inventory-reports',
    INVENTORY_REPORTS_STOCK_ON_HAND: '/pos/inventory-reports/stock-on-hand',
    INVENTORY_REPORTS_WAREHOUSE_SUMMARY:
      '/pos/inventory-reports/warehouse-summary',
    INVENTORY_REPORTS_LOW_STOCK: '/pos/inventory-reports/low-stock',
    INVENTORY_REPORTS_MOVEMENT_LEDGER: '/pos/inventory-reports/movement-ledger',
    INVENTORY_REPORTS_OPEN_PURCHASE_ORDERS:
      '/pos/inventory-reports/open-purchase-orders',
    INVENTORY_REPORTS_RECEIVING_SUMMARY:
      '/pos/inventory-reports/receiving-summary',
    INVENTORY_REPORTS_TRANSFERS: '/pos/inventory-reports/transfers',
    INVENTORY_REPORTS_STOCK_COUNT_VARIANCE:
      '/pos/inventory-reports/stock-count-variance',
    INVENTORY_REPORTS_PO_FILL: '/pos/inventory-reports/po-fill-progress',
    INVENTORY_REPORTS_VALUATION: '/pos/inventory-reports/valuation',
    INVENTORY_REPORTS_AGING: '/pos/inventory-reports/aging',
    INVENTORY_REPORTS_NEAR_EXPIRY: '/pos/inventory-reports/near-expiry',
    INVENTORY_REPORTS_GENERAL_LEDGER: '/pos/inventory-reports/general-ledger',
    REGISTERS: '/pos/registers',
    REGISTERS_NEW: '/pos/registers/new',
    REGISTERS_DETAIL: '/pos/registers/:registerId',
    REGISTERS_DETAIL_PATH: (registerId: string) =>
      `/pos/registers/${registerId}`,
    REGISTERS_EDIT: '/pos/registers/:registerId/edit',
    REGISTERS_EDIT_PATH: (registerId: string) =>
      `/pos/registers/${registerId}/edit`,
    EIS_SETUP: '/pos/eis-setup',
    MACHINE_QR_SETUP: '/pos/machine-qr-setup',
    REGISTER: '/pos/register',
    REGISTER_MORE: '/pos/register/more',
    /** @deprecated Use ROUTES.POS.REGISTER_MORE — kept for redirects */
    REGISTER_ACTIONS: '/pos/register/actions',
    REGISTER_SESSION_SALES: '/pos/register/session-sales',
    RECEIPT_LAYOUT: '/pos/receipt-layout',
    /** @deprecated Use ROUTES.POS.REPORTS_E_JOURNAL — kept for redirects */
    E_JOURNAL: '/pos/e-journal',
    REPORTS: '/pos/reports',
    REPORTS_BACKEND: '/pos/reports/backend',
    REPORTS_E_JOURNAL: '/pos/reports/e-journal',
    REPORTS_ACTIVITY_LOG: '/pos/reports/activity-log',
    REPORTS_EIS_TRANSMISSIONS_LOG: '/pos/reports/eis-transmissions-log',
    REPORTS_BIR_SALES_SUMMARY: '/pos/reports/bir-sales-summary',
    REPORTS_DAILY_CASH: '/pos/reports/daily-cash',
    REPORTS_TERMINAL_ACCOUNTABILITY: '/pos/reports/terminal-accountability',
    REPORTS_DETAILED_SALES: '/pos/reports/detailed-sales',
    REPORTS_ITEMS_SOLD_LEDGER: '/pos/reports/items-sold-ledger',
    REPORTS_SALES_GENERAL_LEDGER: '/pos/reports/sales-general-ledger',
    SALE_DETAIL_PATH: '/pos/sales/:saleId',
    SALE_DETAIL: (saleId: string) => `/pos/sales/${saleId}`,
    REPORTS_X_READING: '/pos/reports/x-reading',
    REPORTS_X_READING_DETAIL_PATH: '/pos/reports/x-reading/:xReadingId',
    REPORTS_X_READING_DETAIL: (xReadingId: string) =>
      `/pos/reports/x-reading/${xReadingId}`,
    REPORTS_X_READING_SAMPLE: '/pos/reports/x-reading/sample',
    REPORTS_VOID: '/pos/reports/void',
    REPORTS_REFUND: '/pos/reports/refund',
    REPORTS_SC_SALES_BOOK: '/pos/reports/senior-citizen-sales-book',
    REPORTS_PWD_SALES_BOOK: '/pos/reports/pwd-sales-book',
    REPORTS_NAAC_SALES_BOOK: '/pos/reports/naac-sales-book',
    REPORTS_SOLO_PARENT_SALES_BOOK: '/pos/reports/solo-parent-sales-book',
    REPORTS_Z_READING: '/pos/reports/z-reading',
    REPORTS_Z_READING_DETAIL_PATH: '/pos/reports/z-reading/:zReadingId',
    REPORTS_Z_READING_DETAIL: (zReadingId: string) =>
      `/pos/reports/z-reading/${zReadingId}`,
    REPORTS_Z_READING_SAMPLE: '/pos/reports/z-reading/sample',
    /** @deprecated Use ROUTES.POS.REPORTS — kept for redirects */
    BIR_REPORTS: '/pos/bir-reports',
  },
  EQUIPMENT: {
    ROOT: '/ppe',
    NEW: '/ppe/new',
    DEPRECIATION_REPORT: '/ppe/depreciation-report',
    DEPRECIATION_VIEW: '/ppe/depreciation/:entryId',
    VIEW: '/ppe/:equipmentId',
    EDIT: '/ppe/:equipmentId/edit',
    DETAIL: (equipmentId: string) => `/ppe/${equipmentId}`,
    DEPRECIATION_DETAIL: (entryId: string) => `/ppe/depreciation/${entryId}`,
    EDIT_DETAIL: (equipmentId: string) => `/ppe/${equipmentId}/edit`,
  },
  HRIS: {
    ROOT: '/hris',
    EMPLOYEES: '/hris/employees',
    ROLE_MANAGER: '/hris/employees/roles',
    EMPLOYEE_NEW: '/hris/employees/new',
    EMPLOYEE_VIEW: '/hris/employees/:employeeId',
    EMPLOYEE_DETAIL: (employeeId: string) => `/hris/employees/${employeeId}`,
    ATTENDANCE: '/hris/attendance',
    LEAVE: '/hris/leave',
    PAYROLL: '/hris/payroll',
    PAYROLL_RUN: '/hris/payroll/runs/:runId',
    PAYROLL_RUN_DETAIL: (runId: string) => `/hris/payroll/runs/${runId}`,
    REPORTS: '/hris/reports',
    SETTINGS: '/hris/settings',
    ME: '/hris/me',
    GUIDE: '/hris/guide',
  },
  PORTAL: {
    ROOT: '/portal',
    ATTENDANCE: '/portal/attendance',
    LEAVE: '/portal/leave',
    PAYSLIPS: '/portal/payslips',
    PROFILE: '/portal/profile',
  },
  CAPITALS: {
    ROOT: '/capitals',
    NEW: '/capitals/new',
    VIEW: '/capitals/:entryId',
    EDIT: '/capitals/:entryId/edit',
    DETAIL: (entryId: string) => `/capitals/${entryId}`,
    EDIT_DETAIL: (entryId: string) => `/capitals/${entryId}/edit`,
  },
  CASH_VOUCHER: {
    NEW: '/cash-vouchers/new',
    VIEW: '/cash-vouchers/:voucherId',
    DETAIL: (voucherId: string) => `/cash-vouchers/${voucherId}`,
  },
  REPORTS: {
    ROOT: '/reports',
    INCOME_STATEMENT: '/reports/income-statement',
    BALANCE_SHEET: '/reports/balance-sheet',
    CASH_FLOW_STATEMENT: '/reports/cash-flow-statement',
    STATEMENT_OF_CHANGES_IN_EQUITY: '/reports/statement-of-changes-in-equity',
    FINANCIAL_STATEMENT: '/reports/financial-statement',
    GENERAL_LEDGER: '/reports/general-ledger',
    TRIAL_BALANCE: '/reports/trial-balance',
    GENERAL_JOURNAL: '/reports/general-journal',
    CASH_RECEIPTS_JOURNAL: '/reports/cash-receipts-journal',
    CASH_DISBURSEMENT_JOURNAL: '/reports/cash-disbursement-journal',
  },
  USER: {
    PROFILE: '/user/profile',
    CHANGE_PASSWORD: '/user/change-password',
    ONBOARDING: '/user/onboarding',
  },
  COMPANY: {
    SETUP: '/user/onboarding',
    LOBBY: '/user/onboarding',
    INVITE: '/company/invite',
    USERS: '/company/users',
    TRANSACTION_PERIOD_LOCKS: '/company/transaction-period-locks',
  },
  COMPANIES: {
    ROOT: '/companies',
  },
  SETTINGS: {
    ROOT: '/settings',
    GENERAL: '/settings/general',
    NOTIFICATIONS: '/settings/notifications',
    CHANGE_PASSWORD: '/settings/change-password',
    COMPANY_SETTINGS: '/settings/company-settings',
    COMPANY_DETAILS: '/settings/company-details',
    COMPANY_DETAILS_EDIT: '/settings/company-details/edit',
    BIR_TAXPAYER_PROFILE: '/settings/bir-taxpayer-profile',
    PROFILE: '/settings/profile',
    USERS: '/settings/users',
    COMPANIES: '/settings/companies',
    TRANSACTION_PERIOD_LOCKS: '/settings/transaction-period-locks',
    BILLING: '/settings/billing',
    CUSTOMER_CARE: '/settings/customer-care',
  },

  SUPER_ADMIN: {
    ROOT: '/super-admin',
    PAYMONGO_CONNECTION: '/super-admin/paymongo-connection',
    PAYMENT_METHODS: '/super-admin/payment-methods',
    SUBSCRIPTION_PLANS: '/super-admin/subscription-plans',
    OPENAI_USAGE: '/super-admin/openai-usage',
    GRANTS: '/super-admin/grants',
  },

  SUPERADMIN: {
    ROOT: '/superadmin',
    INVENTORY_APK: '/superadmin/inventory-apk',
  },

  SHOP: {
    ROOT: '/online-shop',
    /**
     * Unified guest cart across all shops (Shopee/Lazada style); see App.tsx.
     * `/online-shop/cart`. Grouped by shop.
     */
    CART: '/online-shop/cart',
    /**
     * Unified checkout for the whole cart; see App.tsx. `/online-shop/checkout`.
     * Collects buyer info once and places one order per shop.
     */
    CHECKOUT: '/online-shop/checkout',
    /**
     * Unified guest order history across all shops; see App.tsx.
     * `/online-shop/orders`.
     */
    ORDERS: '/online-shop/orders',
    /**
     * Unified guest order detail; see App.tsx. `/online-shop/orders/:orderNumber`.
     * The owning shop handle is carried as a `?h=` query param (the detail API is
     * still handle-scoped) rather than nesting the detail under the storefront.
     */
    ORDER_DETAIL_PATH: '/online-shop/orders/:orderNumber',
    ORDER_DETAIL: (orderNumber: string, handle?: string) =>
      `/online-shop/orders/${encodeURIComponent(orderNumber)}${
        handle ? `?h=${encodeURIComponent(handle)}` : ''
      }`,
    /**
     * Unified guest order success; see App.tsx.
     * `/online-shop/order/:orderId/success?h=:handle`.
     * The owning shop handle is carried as a `?h=` query param (the detail API is
     * still handle-scoped) rather than nesting success under the storefront.
     */
    ORDER_SUCCESS_PATH: '/online-shop/order/:orderId/success',
    ORDER_SUCCESS: (orderId: string, handle?: string) =>
      `/online-shop/order/${encodeURIComponent(orderId)}/success${
        handle ? `?h=${encodeURIComponent(handle)}` : ''
      }`,
    /**
     * Layout mount for all authenticated shop-management pages. Registered in
     * App.tsx with concrete nested routes so paths like `/online-shop/manage/orders`
     * out-rank the public storefront's `/online-shop/:handle/orders` (the static
     * `manage` segment beats the `:handle` param; a splat route would lose to the
     * storefront's concrete nested routes).
     */
    MANAGE: {
      ROOT: '/online-shop/manage',
      ORDERS: '/online-shop/manage/orders',
      ORDER_DETAIL: (orderNumber: string) =>
        `/online-shop/manage/orders/${orderNumber}`,
    },
    /**
     * Public online shop product detail (unauthenticated); see App.tsx.
     * `/online-shop/p/:productId`.
     */
    PRODUCT_PATH: '/online-shop/p/:productId',
    PRODUCT: (productId: string) =>
      `/online-shop/p/${encodeURIComponent(productId)}`,
    /**
     * Public per-store storefront route (unauthenticated); see App.tsx.
     * Namespaced under `/online-shop/s/:handle` so it never collides with the
     * online shop (`/online-shop`), product detail (`/online-shop/p/:productId`),
     * or manage (`/online-shop/manage`).
     */
    STOREFRONT_PATH: '/online-shop/s/:handle/*',
    STOREFRONT: (handle: string) =>
      `/online-shop/s/${encodeURIComponent(handle)}`,
    STOREFRONT_CART: (handle: string) =>
      `/online-shop/s/${encodeURIComponent(handle)}/cart`,
    STOREFRONT_ORDER_HISTORY: (handle: string) =>
      `/online-shop/s/${encodeURIComponent(handle)}/orders`,
  },
  API_KEYS: '/api-keys',

  HELP: '/help',
  HELP_HOW_TO: '/help/how-to/:topic',
  HELP_CUSTOMER_EMAIL_EDITOR: '/help/customer-email-editor',
  HELP_EQUIPMENT: '/help/equipment',
  HELP_POS_INVENTORY: '/help/pos-inventory',
  DOWNLOADABLES: '/downloadables',
  SUPPORT: '/support',
  LEGAL: {
    PRIVACY: '/legal/privacy',
    NPC_CERTIFICATE: '/legal/npc-certificate',
    TERMS: '/legal/terms',
    COOKIES: '/legal/cookies',
    DISCLAIMER: '/legal/disclaimer',
  },
};

const SUPER_ADMIN_PAYMONGO_PATHS = [
  ROUTES.SUPER_ADMIN.PAYMONGO_CONNECTION,
  ROUTES.SUPER_ADMIN.PAYMENT_METHODS,
  ROUTES.SUPER_ADMIN.SUBSCRIPTION_PLANS,
] as const;

export function isSuperAdminPaymongoPath(pathname: string): boolean {
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return SUPER_ADMIN_PAYMONGO_PATHS.some(
    (prefix) => clean === prefix || clean.startsWith(`${prefix}/`),
  );
}

/** Bill detail `?from=` — survives `useCompanyAliasUrlSync` adding `?co=` (location.state does not). */
export const BILLS_LEDGER_NAV_FROM = {
  QUERY_KEY: 'from',
  PAYMENT_HISTORY: 'payment-history',
} as const;

/** Typed by the user to confirm removal of a transaction period lock (matches core-backend). */
export const TRANSACTION_PERIOD_UNLOCK_CONFIRM_PHRASE =
  'unlock transactions' as const;

/** BIR POS provider accreditation block on printed sales invoices (matches core-frontend). */
export const POS_ACCREDITATION_DETAILS = {
  PROVIDER_NAME: 'Abrasoft I.T. Services',
  PROVIDER_ADDRESS: 'Bankers Village 1, Brgy 171, Caloocan City',
  PROVIDER_TIN: '479-870-660-00000',
  PROVIDER_TIN_STATUS: 'Non-VAT Registered',
  ACCREDITATION_NUMBER: 'XXXXXXXX',
  ACCREDITATION_DATE_ISSUED: 'XX/XX/XXXX',
  ACCREDITATION_VALID_UNTIL: 'XX/XX/XXXX',
  PTU_NUMBER: 'Pending PTU assignment',
  PTU_DATE_ISSUED: 'XX/XX/XXXX',
} as const;

/** Public portal / marketing destination for outbound CTAs (e.g. sign-up links). */
export const ABRAFI_URL = 'https://abrafi.ph/';

/** Official AbraFi step-by-step tutorial videos on YouTube. */
export const ABRAFI_YOUTUBE_TUTORIALS_URL = 'https://www.youtube.com/@abrafiph';

/**
 * Vite bakes `VITE_*` at dev/build time; a bind-mounted core-frontend/.env with localhost
 * can win over Docker. When the app is opened via a LAN hostname/IP, align the API host with
 * the page so calls still hit the host-published API port.
 */
function alignApiUrlHostWithPage(fromEnv: string): string {
  if (!fromEnv) return '';
  if (typeof window === 'undefined') {
    return fromEnv;
  }
  const pageHost = window.location.hostname;
  const pageIsLoopback =
    pageHost === 'localhost' || pageHost === '127.0.0.1' || pageHost === '';
  if (pageIsLoopback) {
    return fromEnv;
  }
  try {
    const u = new URL(fromEnv.includes('://') ? fromEnv : `http://${fromEnv}`);
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      u.hostname = pageHost;
      return u.toString().replace(/\/+$/, '');
    }
  } catch {
    /* ignore */
  }
  return fromEnv;
}

export function getReactAppApiUrl(): string {
  return alignApiUrlHostWithPage(
    (import.meta.env.VITE_API_URL || '').replace(/\/+$/, ''),
  );
}

export function getReactAppAuthUrl(): string {
  const raw =
    import.meta.env.VITE_AUTH_URL || import.meta.env.VITE_API_URL || '';
  return alignApiUrlHostWithPage(raw.replace(/\/+$/, ''));
}

// Authentication Constants
export const AUTH_CONSTANTS = {
  // Storage keys
  CODE_VERIFIER_KEY: 'oauth_code_verifier',
  STATE_KEY: 'oauth_state',
  RETURN_PATH_KEY: 'oauth_return_path',
  ACCESS_TOKEN_STORAGE_KEY: 'tkn',
  // Client configuration
  /** Keep in sync with backend AUTH_CLIENT_ID / docker-compose AUTH_CLIENT_ID. */
  CLIENT_ID: import.meta.env.VITE_CLIENT_ID || 'local-dev-client',
  /** Prefer getReactAppApiUrl() — getters support legacy AUTH_CONSTANTS.API_BASE_URL access. */
  get API_BASE_URL() {
    return getReactAppApiUrl();
  },
  /** OAuth authorize/login pages (may be a dedicated auth host). */
  get AUTH_BASE_URL() {
    return getReactAppAuthUrl();
  },
};

export const CACHE_KEYS = {
  USER_PROFILE: 'user_profile_cache_v1',
  ACTIVE_COMPANY_ID_LEGACY: 'active_company_id_cache_v1',
  ACTIVE_COMPANY_ID: 'act_com',
  ACTIVE_COMPANY_ALIAS_MAP: 'act_com_alias_map',
};

/** Session flags for one-shot UI flows (not persisted preferences). */
export const SESSION_STORAGE_KEYS = {
  /** Companies page should skip cached list and refetch (onboarding, FS setup, invite join, etc.). */
  REFETCH_COMPANIES_LIST: 'abrasoft_refetch_companies_list',
  /** @deprecated Use REFETCH_COMPANIES_LIST; kept for in-flight sessions. */
  REFETCH_COMPANIES_AFTER_INVITE: 'abrasoft_refetch_companies_after_invite',
  /** Invite token from landing URL; survives auth redirect for post-onboarding join. */
  PENDING_INVITE_TOKEN: 'abrafi_pending_invite_token_v1',
  /**
   * After accept-invite, join finishes on Dashboard; JSON `{ type, message }` for one toast.
   * Paired with `INVITE_JOIN_FLASH_EVENT` so the dashboard can react in the same tab.
   */
  INVITE_JOIN_RESULT_FLASH: 'abrafi_invite_join_result_flash_v1',
  /** First visit to dashboard after creating a company via onboarding. */
  SHOW_COMPANY_CREATED_SUCCESS_MODAL: 'abrafi_show_company_created_success_v1',
  /** Companies page should show the create-success toast on next visit (e.g. onboarding). */
  COMPANIES_CREATE_SUCCESS_TOAST: 'abrafi_companies_create_success_toast_v1',
  /** After create+collect-now, open the collection receipt once on invoice detail. */
  OPEN_COLLECTION_RECEIPT_INVOICE_ID:
    'abrafi_ar_open_collection_receipt_invoice_id_v1',
};

/** Same-tab custom event: invite join wrote `SESSION_STORAGE_KEYS.INVITE_JOIN_RESULT_FLASH`. */
export const INVITE_JOIN_FLASH_EVENT = 'abrafi-invite-join-flash';

/** Same-tab: onboarding set `SHOW_COMPANY_CREATED_SUCCESS_MODAL` before navigating to dashboard. */
export const COMPANY_CREATED_SUCCESS_MODAL_EVENT =
  'abrafi-company-created-success-modal';

export const URL_PARAM_KEYS = {
  /**
   * Active company in the URL (`?co=<companyUuid>`).
   * Legacy bookmarks may still use a 1-based list index (`?co=1`); those keep
   * working on read and are rewritten to the UUID on sync.
   */
  COMPANY_ALIAS: 'co',
};

// Encryption Constants
export const ENCRYPTION_CONSTANTS = {
  APP_SALT:
    import.meta.env.VITE_ENCRYPTION_SALT || 'abrasoft-accounting-app-salt-2024',
  USER_SEED_KEY: 'user_encryption_seed',
};

// IndexedDB Constants
export const INDEXED_DB_CONSTANTS = {
  DB_NAME: 'abrasoft_auth_db',
  DB_VERSION: 1,
  STORE_NAME: 'tokens',
};

/**
 * Generic offline synchronization and caching utilities using IndexedDB
 */

export const OFFLINE_DB_NAME = 'abrasoft_offline_db';
export const OFFLINE_DB_VERSION = 2; // Incremented to fix open failures
export const PENDING_SYNC_STORE = 'pending_sync';
export const FAILED_SYNC_STORE = 'fss';
export const CACHE_STORE = 'cache';
